/**
 * 业务页签内的「检查项总览」表 + 检查项切换栏（design D15 第 2、3 层）。
 *
 * 一行一个检查项：检查项 | 类型 | 运行 case | 通过 | 不通过 | 通过率 | 主要指标 | 查看。
 * - 运行 case = 场景的 evaluator_list 里有它、或有它的结果 / 指标块；不适用的场景不计入；
 * - 通过 = 运行 − 上报失败（算法侧对通过项可能不回传结果行，与 daily 同口径）；
 * - 主要指标只对指标型有值：ALL 行跨 case 重算的召回 / 精确率 / IoU（OD 为平均 IoU）；
 *   OCC Benchmark 有 halo 数据时改为 halo 口径的 mIoU / 占据 IoU / Macro RayIoU / GOD F1。
 *
 * 切换栏是一排胶囊按钮，不通过数用红色角标；同一时间只显示一个检查项的详情。
 */
import { Button, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import type { PerceptionCheckerSummary } from '@/apis/simulation';

import { EMPTY, fmtCount, fmtPp, fmtRatio, isCellUnit } from '../format';

/** OCC FP 人工 ROI checker：判定型，但总览要显示帧级指标与各状态 case 数。 */
const OCC_ROI_CHECKERS = new Set([
  'occ_false_detection_checker',
  'occ_obstacle_false_detection_checker',
  'occ_road_false_checker',
  'occ_obstacle_road_false_checker',
]);
export const isOccRoiChecker = (checker: string): boolean => OCC_ROI_CHECKERS.has(checker);

/** 主要指标一行文案：召回 · 精确率 · IoU（cell）/ 平均 IoU（box）。判定型返回 EMPTY。 */
export const headlineText = (s: PerceptionCheckerSummary): string => {
  if ((s.kind !== 'metrics' && !isOccRoiChecker(s.checker)) || !s.headline) return EMPTY;
  const h = s.headline;
  if (s.task === 'tld' && s.checker === 'perception_tld_state_check') {
    return `颜色一致率 ${fmtRatio(h.colorConsistency)} · 时间对齐率 ${fmtRatio(h.alignedFrameRatio)}`;
  }
  if (h.haloCases) {
    return `mIoU ${fmtRatio(h.miou)} · 占据 IoU ${fmtRatio(h.occupancyIou)} · Macro RayIoU ${fmtRatio(
      h.macroRayiou,
    )} · GOD F1 ${fmtRatio(h.godF1)}`;
  }
  if (isOccRoiChecker(s.checker)) {
    return `误检率 ${fmtRatio(h.occFailRate, 1)} · 失败 ${fmtCount(h.occFailFrames)}/${fmtCount(
      h.occEligibleFrames,
    )} 帧 · 最长连续 ${fmtCount(h.occMaxConsecutiveFail)} 帧 · ok ${fmtCount(
      h.occOkCases,
    )} / warning ${fmtCount(h.occWarningCases)} / error ${fmtCount(h.occErrorCases)} / skip ${fmtCount(
      h.occSkipCases,
    )}`;
  }
  const iou = isCellUnit(s.unit)
    ? `IoU ${fmtRatio(h.iou)}`
    : `平均 IoU ${fmtRatio(h.meanIou)}`;
  return `召回 ${fmtRatio(h.recall)} · 精确率 ${fmtRatio(h.precision)} · ${iou}`;
};

/** 选了基线时的召回 Δ（pp）；任一侧缺失返回 null。 */
const recallDeltaPp = (s: PerceptionCheckerSummary): number | null => {
  const curr = s.headline?.recall;
  const base = s.base?.headline?.recall;
  if (curr === null || curr === undefined || base === null || base === undefined) return null;
  return (curr - base) * 100;
};

export const checkerKindTag = (kind: string) =>
  kind === 'metrics' ? (
    <Tooltip title="输出数值指标，也可以附带逐 case 阈值达标结论；不表示只有指标、没有判定">
      <Tag color="blue" bordered={false} style={{ marginInlineEnd: 0 }}>
        指标型
      </Tag>
    </Tooltip>
  ) : (
    <Tag bordered={false} style={{ marginInlineEnd: 0 }}>
      判定型
    </Tag>
  );

const checkerNameCell = (s: PerceptionCheckerSummary) => (
  <div style={{ lineHeight: 1.35 }}>
    <code className="pe-ck-code">{s.checker}</code>
    {s.checkerCn && s.checkerCn !== s.checker && (
      <div style={{ fontSize: 11, color: '#9b9da3' }}>{s.checkerCn}</div>
    )}
  </div>
);

interface CheckerOverviewTableProps {
  checkers: PerceptionCheckerSummary[];
  selected: string | undefined;
  onSelect: (checker: string) => void;
  /** 是否选了基线 Job（选了才显示基线通过率与召回 Δ） */
  withBase: boolean;
}

export function CheckerOverviewTable({
  checkers,
  selected,
  onSelect,
  withBase,
}: CheckerOverviewTableProps) {
  const tldOnly = checkers.length > 0 && checkers.every(
    (s) => s.task === 'tld' && s.checker === 'perception_tld_state_check',
  );
  const columns: ColumnsType<PerceptionCheckerSummary> = [
    { title: '检查项', key: 'checker', width: 240, render: (_, s) => checkerNameCell(s) },
    { title: '类型', key: 'kind', width: 80, render: (_, s) => checkerKindTag(s.kind) },
    {
      title: <Tooltip title="名单里有它或有它结果的场景数；不适用的场景不计入">运行 case</Tooltip>,
      dataIndex: 'runCases',
      key: 'runCases',
      width: 90,
      align: 'right',
      render: fmtCount,
    },
    {
      title: <Tooltip title={tldOnly ? '逐 case 的 checker 达标结论，不是 Job 成功数' : '运行 − 上报失败；检查项结果不等于 Job 状态'}>{tldOnly ? '达标' : '通过'}</Tooltip>,
      dataIndex: 'passCases',
      key: 'passCases',
      width: 80,
      align: 'right',
      render: (v, s) => s.checker === 'lane_polyline_check' ? '未配置' : fmtCount(v),
    },
    {
      title: tldOnly ? '未达标' : '不通过',
      dataIndex: 'failCases',
      key: 'failCases',
      width: 80,
      align: 'right',
      render: (v: number) => (
        <span style={{ color: v > 0 ? '#e03131' : undefined }}>{fmtCount(v)}</span>
      ),
    },
    {
      title: tldOnly ? '达标率' : '通过率',
      key: 'passRate',
      width: withBase ? 150 : 90,
      align: 'right',
      render: (_, s) => (
        <span>
          {fmtRatio(s.passRate, 1)}
          {withBase && (
            <span style={{ color: '#9b9da3', marginLeft: 6, fontSize: 11 }}>
              基线 {s.base ? fmtRatio(s.base.passRate, 1) : EMPTY}
            </span>
          )}
        </span>
      ),
    },
    {
      title: (
        <Tooltip title="指标型：ALL 行跨 case 计数求和后重算；判定型无指标">主要指标</Tooltip>
      ),
      key: 'headline',
      width: 400,
      render: (_, s) => {
        const d = withBase ? recallDeltaPp(s) : null;
        return (
          <span>
            {s.kind === 'metrics' || isOccRoiChecker(s.checker)
              ? headlineText(s)
              : (s.headline?.message ?? '不通过的 case 见详情')}
            {d !== null && (
              <span style={{ marginLeft: 6, fontSize: 11, color: d >= 0 ? '#2f9e44' : '#e03131' }}>
                召回 {fmtPp(d)}
              </span>
            )}
          </span>
        );
      },
    },
    {
      title: '',
      key: 'view',
      width: 70,
      render: (_, s) => (
        <Button
          size="small"
          type={s.checker === selected ? 'primary' : 'default'}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(s.checker);
          }}
        >
          查看
        </Button>
      ),
    },
  ];

  return (
    <Table<PerceptionCheckerSummary>
      rowKey="checker"
      size="small"
      dataSource={checkers}
      columns={columns}
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: 1100 }}
      onRow={(s) => ({ onClick: () => onSelect(s.checker), style: { cursor: 'pointer' } })}
      rowClassName={(s) => (s.checker === selected ? 'pe-ck-row--selected' : '')}
    />
  );
}

interface CheckerSwitcherProps {
  checkers: PerceptionCheckerSummary[];
  selected: string | undefined;
  onSelect: (checker: string) => void;
}

/** 检查项胶囊切换栏：不通过数用红色角标。 */
export function CheckerSwitcher({ checkers, selected, onSelect }: CheckerSwitcherProps) {
  return (
    <div className="pe-ck-bar" role="tablist" aria-label="检查项">
      {checkers.map((s) => {
        const active = s.checker === selected;
        return (
          <button
            key={s.checker}
            type="button"
            role="tab"
            aria-selected={active}
            className={`pe-ck${active ? ' pe-ck--active' : ''}`}
            onClick={() => onSelect(s.checker)}
            title={s.checkerCn || s.checker}
          >
            {s.checker}
            {s.failCases > 0 && <span className="pe-ck-badge">{s.failCases}</span>}
          </button>
        );
      })}
    </div>
  );
}
